import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../layouts/DashboardLayout';
import { Button } from '@/components/ui/button';
import { 
  Calendar, 
  Copy, 
  PlusCircle, 
  Share2, 
  BarChart3, 
  Users, 
  Clock, 
  ArrowUpRight 
} from 'lucide-react';
import { toast } from 'sonner';
import BookingHistory from '@/components/dashboard/BookingHistory';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  ChartContainer, 
  ChartTooltip, 
  ChartTooltipContent 
} from '@/components/ui/chart';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip } from 'recharts';
import { QuickStats } from '@/components/dashboard/QuickStats';
import { ActionCenter } from '@/components/dashboard/ActionCenter';

const Dashboard: React.FC = () => {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  
  const { data: bookingStats } = useQuery({
    queryKey: ['booking-stats'],
    queryFn: async () => {
      const username = user?.name?.toLowerCase().replace(/\s+/g, '');
      
      if (!username) {
        throw new Error('No username available');
      }
      
      const { data: bookings, error } = await supabase
        .from('bookings')
        .select('*')
        .eq('host_username', username);
        
      if (error) {
        throw error;
      }
      
      // Calculate booking statistics
      const confirmed = bookings.filter(b => b.status === 'confirmed').length;
      const cancelled = bookings.filter(b => b.status === 'cancelled').length;
      const total = bookings.length;
      
      // Generate mock data for the chart (last 7 days)
      const last7Days = Array.from({ length: 7 }, (_, i) => {
        const date = new Date();
        date.setDate(date.getDate() - i);
        return date.toISOString().split('T')[0];
      }).reverse();
      
      const chartData = last7Days.map(date => {
        const bookingsOnDay = bookings.filter(b => b.date === date).length;
        return {
          date: new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          bookings: bookingsOnDay
        };
      });
      
      return {
        confirmed,
        cancelled,
        total,
        chartData
      };
    },
    enabled: !!user,
  });

  const handleCreateBookingLink = () => {
    navigate('/create-booking-link');
  };

  const copyPersonalLink = () => {
    if (user) {
      const username = user.name.toLowerCase().replace(/\s+/g, '');
      const link = `${window.location.origin}/${username}`;
      navigator.clipboard.writeText(link)
        .then(() => {
          toast.success('Link copied to clipboard!');
        })
        .catch((err) => {
          console.error('Failed to copy link: ', err);
          toast.error('Failed to copy link');
        });
    }
  };

  if (!isAuthenticated) {
    return null;
  }

  const username = user?.name?.toLowerCase().replace(/\s+/g, '');
  const personalLink = `${window.location.origin}/${username}`;

  return (
    <DashboardLayout>
      <div className="container max-w-6xl mx-auto px-4 py-8">
        <div className="flex flex-col md:flex-row gap-6 md:items-center md:justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold">Dashboard</h1>
            <p className="text-muted-foreground mt-1">
              Manage your booking links and view analytics
            </p>
          </div>
          <Button size="lg" onClick={handleCreateBookingLink} className="gap-2">
            <PlusCircle className="h-5 w-5" />
            Create Booking Link
          </Button>
        </div>

        <Tabs defaultValue="overview" value={activeTab} onValueChange={setActiveTab} className="mb-8">
          <TabsList className="mb-6">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="analytics">Analytics</TabsTrigger>
            <TabsTrigger value="bookings">Bookings</TabsTrigger>
          </TabsList>
          
          <TabsContent value="overview">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 space-y-6">
                <QuickStats />
                <ActionCenter />
              </div>
              <div>
                <BookingHistory />
              </div>
            </div>
          </TabsContent>
          
          <TabsContent value="analytics">
            <Card>
              <CardHeader>
                <CardTitle>Booking Analytics</CardTitle>
                <CardDescription>Comprehensive view of your booking performance</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  {bookingStats?.chartData && (
                    <ChartContainer
                      config={{
                        bookings: {
                          label: "Bookings",
                          color: "hsl(var(--primary))",
                        }
                      }}
                    >
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={bookingStats.chartData}>
                          <XAxis 
                            dataKey="date" 
                            stroke="hsl(var(--muted-foreground))" 
                            fontSize={12}
                            tickLine={false}
                            axisLine={false}
                          />
                          <YAxis 
                            stroke="hsl(var(--muted-foreground))" 
                            fontSize={12}
                            tickLine={false}
                            axisLine={false}
                            allowDecimals={false}
                          />
                          <Bar 
                            dataKey="bookings" 
                            fill="hsl(var(--primary))" 
                            radius={[4, 4, 0, 0]}
                            barSize={40}
                          />
                          <ChartTooltip
                            content={
                              <ChartTooltipContent />
                            }
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    </ChartContainer>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium">Total Bookings</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">{bookingStats?.total || 0}</div>
                      <p className="text-xs text-muted-foreground mt-1">All time</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium">Confirmation Rate</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">
                        {bookingStats?.total 
                          ? Math.round((bookingStats.confirmed / bookingStats.total) * 100) 
                          : 0}%
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">Based on confirmed bookings</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium">Cancellation Rate</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">
                        {bookingStats?.total 
                          ? Math.round((bookingStats.cancelled / bookingStats.total) * 100) 
                          : 0}%
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">Based on cancelled bookings</p>
                    </CardContent>
                  </Card>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="bookings">
            <div className="grid gap-4">
              <BookingHistory fullWidth />
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
};

export default Dashboard;
